// RsiWidget/RsiEngine.js
// Engine Pattern (rk=f03ae8b6) — extends EngineCleanupBase, singleton export.
// bootup+fetch() called from PageContent at app boot (same as Sma200Engine), RMQ
// pushes daily per-asset-per-period updates from there on via onRsiReceived (wired in
// PageContentSRHandlers once that side exists).
import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import rsiCache from './RsiCache';
import rsiFetcher from './RsiFetcher';

const _name = 'RsiEngine';

class RsiEngine extends EngineCleanupBase {
    bootup = () => {
        this.initialize(_name);
    };

    /**
     * Initial fetch for ALL assets AND ALL periods (sid=0, period=0) — called once
     * from PageContent alongside the other engines' fetch/fetchAll calls.
     * @returns {Promise<void>}
     */
    fetch = async () => {
        try {
            const items = await rsiFetcher.fetchAll();
            if (!items.length) return;

            rsiCache.addOrUpdate(items);
            this._notify(null);
        } catch (error) {
            this.logError('fetch', error.message);
        }
    };

    /**
     * RMQ push handler. CacheBase2.addOrUpdate() requires an array (it calls
     * data.forEach) — wraps a bare single object into one.
     * @param {object|object[]} data {rk,root,sid,avgGain,avgLoss,value,price,ems}
     */
    onRsiReceived = (data) => {
        try {
            const items = Array.isArray(data) ? data : [data];
            this.logDebug('onRsiReceived', `items received ${items.length}`);

            rsiCache.addOrUpdate(items);

            const sids = new Set(items.map((i) => i.sid));
            sids.forEach((sid) => this._notify(sid));
        } catch (error) {
            this.logError('onRsiReceived', error.message);
        }
    };

    /**
     * Latest RSI slot for one asset + period.
     * @param {number} sid
     * @param {number} period
     * @returns {{value:number,avgGain:number,avgLoss:number,price:number,ems:number}|null}
     */
    getState = (sid, period) => {
        const root = `${sid}_${period}`;
        const items = rsiCache.getAll().filter((i) => i.root === root);
        if (!items.length) return null;

        return items.reduce((latest, i) => (i.ems > latest.ems ? i : latest));
    };

    /**
     * @param {number|null} sid - which asset changed, or null for a bulk refresh
     */
    _notify = (sid) => {
        ComponentNotifier.notify(_name, 'dataChanged', { sid });
    };
}

const rsiEngine = new RsiEngine();
export default rsiEngine;
